import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from 'xlsx';
import { doc, getDoc } from "firebase/firestore";
import { auth, db, appId } from "../firebase";
import { useModal } from "../contexts/ModalContext";
import { matchSearch } from "../utils/chosung";
import { DESIGN_TOKENS } from "../constants/designTokens";
import { DEFAULT_THEME } from "../constants/theme";
import { useSoteriaAttendance } from "../hooks/useSoteriaAttendance"; // [NEW] Soteria 연동 훅
import useAttendanceEscapeHandler from "./attendance/useAttendanceEscapeHandler";
import useAttendanceActions from "./attendance/useAttendanceActions";
import useAttendanceDownloadAction from "./attendance/useAttendanceDownloadAction";
import useAttendanceListSwipe from "./attendance/useAttendanceListSwipe";
import useAttendanceSave from "./attendance/useAttendanceSave";
import useAttendanceShareActions from "./attendance/useAttendanceShareActions";
import useAttendanceSoteriaSync from "./attendance/useAttendanceSoteriaSync";
import useAttendanceDragSelect from "./attendance/useAttendanceDragSelect";
import AttendanceDesktopContent from "./attendance/AttendanceDesktopContent";
import AttendanceDetailModal from "./attendance/AttendanceDetailModal";
import AttendanceGlassCard from "./attendance/AttendanceGlassCard";
import AttendanceMobileContent from "./attendance/AttendanceMobileContent";
import {
    buildDailyAttendanceStats,
    buildFilteredAttendanceTargets,
    buildYearlyAttendanceData,
    extractAttendanceLogPayload,
    getAllSundaysInYear,
    getAttendanceCheckedCount,
    getSundaysInMonth,
    mapAttendanceForTargetList,
} from "./attendance/attendanceViewDataUtils";

export default function AttendanceView({
    students,
    teachers = [],
    attendanceLogs = [],
    initialDate,
    onUpdate,
    onDelete,
    demoAction,
    searchQuery,
    setSearchQuery,
    targetGroup,
    setTargetGroup,
    selectedDepartment,
    theme = DEFAULT_THEME,
    onRefreshLogs, // [NEW] Refresh trigger
    userProfile = null, // [NEW] For organization-based attendance
    onSaveAttendance, // [NEW] For achievement tracking
    visitLogs = []
}) {
    const brand = DESIGN_TOKENS.brand;
    const [activeTab, setActiveTab] = useState("daily");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // const [targetGroup, setTargetGroup] = useState("students"); // Lifted to App.js
    const targetList = targetGroup === 'students' ? students : teachers;

    const [viewMode, setViewMode] = useState("list"); // 'card' or 'list'
    const [currentDate, setCurrentDate] = useState(new Date().toISOString().slice(0, 10));
    const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear().toString());
    // const [searchQuery, setSearchQuery] = useState(""); // Lifted to App.js
    const [isLoading, setIsLoading] = useState(false);
    const { openAlert, openConfirm } = useModal(); // openConfirm 추가


    const [dailyAttendance, setDailyAttendance] = useState({});
    const [savedCheckCount, setSavedCheckCount] = useState(0); // [NEW] 마지막 저장 시 출석 인원 수 (배지 중복 방지)
    const [monthlyData, setMonthlyData] = useState({});
    const [yearlyData, setYearlyData] = useState({});
    const [sundaysInMonth, setSundaysInMonth] = useState([]);

    const [sortKey, setSortKey] = useState(targetGroup === 'teachers' ? "name" : "grade"); // [MOD] 학생 기본: 학년순, 교사 기본: 이름순
    const [sortOrder, setSortOrder] = useState("asc"); // [NEW] 정렬 순서

    // [NEW] 탭 변경 시 기본 정렬로 리셋
    useEffect(() => {
        setSortKey(targetGroup === 'teachers' ? "name" : "grade");
    }, [targetGroup]);

    const [selectedIds, setSelectedIds] = useState([]);
    const [viewingStudent, setViewingStudent] = useState(null);
    const [bulkHoverState, setBulkHoverState] = useState(null);
    const [animateChart, setAnimateChart] = useState(false);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false); // [NEW] 다운로드 메뉴 상태
    const scrollContainerRef = React.useRef(null); // [NEW] 자동 스크롤을 위한 컨테이너 ref

    const { onListSwipeEnd, onTouchMove, onTouchStart } = useAttendanceListSwipe({
        setTargetGroup,
        targetGroup,
    });

    const filteredStudents = useMemo(() => (
        buildFilteredAttendanceTargets({
            targetList,
            searchQuery,
            selectedDepartment,
            targetGroup,
            dailyAttendance,
            sortKey,
            sortOrder,
            matchSearchFn: matchSearch,
            attendanceLogs,
            visitLogs,
        })
    ), [targetList, searchQuery, sortKey, sortOrder, dailyAttendance, selectedDepartment, targetGroup, attendanceLogs, visitLogs]);

    useAttendanceEscapeHandler({ setViewingStudent });

    // [NEW] initialDate 변경 시 날짜 업데이트
    useEffect(() => {
        if (initialDate) {
            setCurrentDate(new Date(initialDate).toISOString().slice(0, 10));
            setActiveTab("daily");
        }
    }, [initialDate]);


    useEffect(() => {
        if (activeTab === 'yearly') {
            setTimeout(() => setAnimateChart(true), 100);
        } else {
            setAnimateChart(false);
        }
    }, [activeTab]);

    // [NEW] Mobile Default to List View (per user request)
    useEffect(() => {
        if (window.innerWidth < 768) setViewMode("list");
    }, []);

    const {
        changeDate,
        changeMonth,
        changeYear,
        handleStatusChange,
        handleSelectAll,
        handleSelectRow,
        handleBulkStatusChange,
        handleClearSelection,
        handleYearMonthClick,
        handleJumpToDaily,
    } = useAttendanceActions({
        currentDate,
        currentMonth,
        currentYear,
        dailyAttendance,
        filteredStudents,
        selectedIds,
        setActiveTab,
        setCurrentDate,
        setCurrentMonth,
        setCurrentYear,
        setDailyAttendance,
        setSelectedIds,
    });

    const {
        handleMarkDragStart,
        handleSelectDragStart,
        handleDragOver,
        handleTouchMove,
        isDragging,
        dragStatus,
    } = useAttendanceDragSelect(handleStatusChange, setSelectedIds, scrollContainerRef);

    const { handleSaveDaily } = useAttendanceSave({
        currentDate,
        dailyAttendance,
        onRefreshLogs,
        onSaveAttendance,
        openAlert,
        savedCheckCount,
        setSavedCheckCount,
        userProfile,
    });
    const dailyStats = useMemo(
        () => buildDailyAttendanceStats(filteredStudents, dailyAttendance),
        [dailyAttendance, filteredStudents]
    );
    const { handleShareStats, handleShareViewingStudent } = useAttendanceShareActions({
        currentDate,
        dailyStats,
        filteredStudents,
        openAlert,
        openConfirm,
        viewingStudent,
    });
    const { handleDownloadExcel } = useAttendanceDownloadAction({
        XLSX,
        activeTab,
        currentDate,
        currentMonth,
        dailyAttendance,
        filteredStudents,
        monthlyData,
        onYearlyUnsupportedDownload: () => showToast("연간 데이터 다운로드는 준비중입니다.", "info"),
        sundaysInMonth,
    });

    // [OPTIMIZED] 🚀 Phase 2: 일별 탭 Firestore 최적화
    // props로 받은 attendanceLogs를 먼저 확인하고, 없으면 Firestore에서 조회
    useEffect(() => {
        if (activeTab !== "daily") return;

        const fetchDaily = async () => {
            if (!auth.currentUser) return;
            setIsLoading(true);
            setDailyAttendance({});

            // [Fix] Guest Mode: Use LocalStorage to avoid permission errors
            if (auth.currentUser.isAnonymous) {
                const saved = localStorage.getItem(`attendance_${auth.currentUser.uid}_${currentDate}`);
                const savedData = saved ? JSON.parse(saved) : {};
                const newMap = mapAttendanceForTargetList(targetList, savedData);
                setDailyAttendance(newMap);
                setSelectedIds([]);
                setIsLoading(false);
                return;
            }

            // [OPTIMIZED] 🚀 props의 attendanceLogs에서 먼저 찾기
            const existingLog = attendanceLogs.find(log => log.date === currentDate);

            if (existingLog) {
                // 이미 있는 데이터는 재사용 (Firestore 읽기 0회!)
                const savedData = extractAttendanceLogPayload(existingLog);
                const newMap = mapAttendanceForTargetList(targetList, savedData);
                setDailyAttendance(newMap);
                // [FIX] 저장된 출석 인원 수 초기화 (배지 중복 방지)
                const initialCheckCount = getAttendanceCheckedCount(newMap);
                setSavedCheckCount(initialCheckCount);
                setSelectedIds([]);
                setIsLoading(false);
                return;
            }

            // 캐시에 없으면 Firestore에서 조회
            try {
                const organizationId = userProfile?.organizationId;
                let docRef;
                if (organizationId) {
                    docRef = doc(db, "organizations", organizationId, "attendance", currentDate);
                } else {
                    docRef = doc(db, "artifacts", appId, "users", auth.currentUser.uid, "attendance", currentDate);
                }
                const docSnap = await getDoc(docRef);
                const savedData = docSnap.exists() ? docSnap.data() : {};
                const newMap = mapAttendanceForTargetList(targetList, savedData);
                setDailyAttendance(newMap);
                // [FIX] 저장된 출석 인원 수 초기화 (배지 중복 방지)
                const initialCheckCount = getAttendanceCheckedCount(newMap);
                setSavedCheckCount(initialCheckCount);
                setSelectedIds([]);
            } catch (e) { console.error(e); } finally { setIsLoading(false); }
        };

        if (targetList.length > 0) fetchDaily();
    }, [currentDate, targetList, activeTab, targetGroup, userProfile, attendanceLogs]);

    // [OPTIMIZED] 🚀 Phase 2: 월간 탭 Firestore 최적화
    // props로 받은 attendanceLogs를 먼저 활용하고, 없는 데이터만 조회
    useEffect(() => {
        if (activeTab !== "monthly") return;

        const fetchMonthly = async () => {
            if (!auth.currentUser) return;
            setIsLoading(true);
            const sundays = getSundaysInMonth(currentMonth);
            setSundaysInMonth(sundays);
            const newMonthlyData = {};

            // [Fix] Guest Mode: Use LocalStorage
            if (auth.currentUser.isAnonymous) {
                sundays.forEach(dateStr => {
                    const saved = localStorage.getItem(`attendance_${auth.currentUser.uid}_${dateStr}`);
                    newMonthlyData[dateStr] = saved ? JSON.parse(saved) : {};
                });
                setMonthlyData(newMonthlyData);
                setIsLoading(false);
                return;
            }

            // [OPTIMIZED] 🚀 props의 attendanceLogs 먼저 활용
            // attendanceLogs에 이미 있는 데이터는 재사용, 없는 것만 조회
            const missingDates = [];
            sundays.forEach(dateStr => {
                const existingLog = attendanceLogs.find(log => log.date === dateStr);
                if (existingLog) {
                    // 이미 있는 데이터는 재사용 (Firestore 읽기 0회!)
                    newMonthlyData[dateStr] = extractAttendanceLogPayload(existingLog);
                } else {
                    missingDates.push(dateStr);
                }
            });

            // 없는 데이터만 Firestore에서 조회 (최소 읽기)
            if (missingDates.length > 0) {
                try {
                    await Promise.all(missingDates.map(async (dateStr) => {
                        const organizationId = userProfile?.organizationId;
                        let docRef;
                        if (organizationId) {
                            docRef = doc(db, "organizations", organizationId, "attendance", dateStr);
                        } else {
                            docRef = doc(db, "artifacts", appId, "users", auth.currentUser.uid, "attendance", dateStr);
                        }
                        const docSnap = await getDoc(docRef);
                        newMonthlyData[dateStr] = docSnap.exists() ? docSnap.data() : {};
                    }));
                } catch (e) { console.error(e); }
            }

            setMonthlyData(newMonthlyData);
            setIsLoading(false);
        };

        fetchMonthly();
    }, [currentMonth, activeTab, userProfile, attendanceLogs]);

    // [OPTIMIZED] 🚀 Phase 2: 연간 탭 Firestore 최적화 (가장 중요!)
    // 기존: 52주 모두 Firestore 조회 (52 reads)
    // 변경: props의 attendanceLogs 먼저 활용, 없는 것만 조회 (거의 0 reads)
    useEffect(() => {
        if (activeTab !== "yearly") return;

        const fetchYearly = async () => {
            if (!auth.currentUser) return;
            setIsLoading(true);

            try {
                const allSundays = getAllSundaysInYear(currentYear);

                const fetchedData = {};

                // [Fix] Guest Mode: Use LocalStorage
                if (auth.currentUser.isAnonymous) {
                    allSundays.forEach(dateStr => {
                        const saved = localStorage.getItem(`attendance_${auth.currentUser.uid}_${dateStr}`);
                        if (saved) fetchedData[dateStr] = JSON.parse(saved);
                    });
                } else {
                    // [OPTIMIZED] 🚀 props의 attendanceLogs 먼저 활용
                    const missingDates = [];

                    allSundays.forEach(dateStr => {
                        const existingLog = attendanceLogs.find(log => log.date === dateStr);
                        if (existingLog) {
                            // 이미 있는 데이터는 재사용 (Firestore 읽기 0회!)
                            fetchedData[dateStr] = extractAttendanceLogPayload(existingLog);
                        } else {
                            missingDates.push(dateStr);
                        }
                    });

                    // [OPTIMIZED] 없는 데이터만 Firestore에서 조회
                    // Firebase 무료 플랜 고려: 한 번에 너무 많이 조회하면 제한될 수 있으니 체크
                    if (missingDates.length > 0) {
                        await Promise.all(missingDates.map(async (dateStr) => {
                            const organizationId = userProfile?.organizationId;
                            let docRef;
                            if (organizationId) {
                                docRef = doc(db, "organizations", organizationId, "attendance", dateStr);
                            } else {
                                docRef = doc(db, "artifacts", appId, "users", auth.currentUser.uid, "attendance", dateStr);
                            }
                            const docSnap = await getDoc(docRef);
                            if (docSnap.exists()) { fetchedData[dateStr] = docSnap.data(); }
                        }));
                    }
                }

                const aggregated = buildYearlyAttendanceData({
                    targetList,
                    currentYear,
                    allSundays,
                    fetchedData,
                });

                setYearlyData(aggregated);
            } catch (e) { console.error(e); } finally { setIsLoading(false); }
        };

        fetchYearly();
    }, [currentYear, activeTab, targetList, userProfile, attendanceLogs]);

    // [NEW] SOTERIA 연동 (QR 출석 불러오기)
    const { getSoteriaAttendees, loading: isSoteriaLoading } = useSoteriaAttendance();
    const { handleSyncSoteria } = useAttendanceSoteriaSync({
        currentDate,
        dailyAttendance,
        getSoteriaAttendees,
        openAlert,
        setDailyAttendance,
        targetList,
    });

    const ListWrapper = isMobile ? 'div' : AttendanceGlassCard;

    return (
        <div className={`w-full font-sans relative ${isMobile ? 'h-full' : 'min-h-screen pt-4 px-4 pb-24'}`}>
            {isMobile && (
                <AttendanceMobileContent
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    bulkHoverState={bulkHoverState}
                    changeDate={changeDate}
                    changeMonth={changeMonth}
                    changeYear={changeYear}
                    currentDate={currentDate}
                    currentMonth={currentMonth}
                    currentYear={currentYear}
                    dailyAttendance={dailyAttendance}
                    dailyStats={dailyStats}
                    filteredStudents={filteredStudents}
                    handleBulkStatusChange={handleBulkStatusChange}
                    handleClearSelection={handleClearSelection}
                    handleSaveDaily={handleSaveDaily}
                    handleSelectAll={handleSelectAll}
                    handleSelectRow={handleSelectRow}
                    handleStatusChange={handleStatusChange}
                    handleSyncSoteria={handleSyncSoteria}
                    isSoteriaLoading={isSoteriaLoading}
                    onListSwipeEnd={onListSwipeEnd}
                    onTouchMove={onTouchMove}
                    onTouchStart={onTouchStart}
                    searchQuery={searchQuery}
                    selectedDepartment={selectedDepartment}
                    selectedIds={selectedIds}
                    setCurrentDate={setCurrentDate}
                    setCurrentMonth={setCurrentMonth}
                    setCurrentYear={setCurrentYear}
                    setSearchQuery={setSearchQuery}
                    setSortKey={setSortKey}
                    setSortOrder={setSortOrder}
                    sortKey={sortKey}
                    sortOrder={sortOrder}
                    targetGroup={targetGroup}
                    handleTouchMove={handleTouchMove}
                    handleSelectDragStart={handleSelectDragStart}
                    handleMarkDragStart={handleMarkDragStart}
                    handleDragOver={handleDragOver}
                    isDragging={isDragging}
                    dragStatus={dragStatus}
                    setSelectedIds={setSelectedIds}
                    setBulkHoverState={setBulkHoverState}
                    theme={theme}
                />
            )}

            {!isMobile && (
                <AttendanceDesktopContent
                    GlassCard={AttendanceGlassCard}
                    ListWrapper={ListWrapper}
                    activeTab={activeTab}
                    animateChart={animateChart}
                    brand={brand}
                    bulkHoverState={bulkHoverState}
                    changeDate={changeDate}
                    changeMonth={changeMonth}
                    changeYear={changeYear}
                    currentDate={currentDate}
                    currentMonth={currentMonth}
                    currentYear={currentYear}
                    dailyAttendance={dailyAttendance}
                    dailyStats={dailyStats}
                    filteredStudents={filteredStudents}
                    handleBulkStatusChange={handleBulkStatusChange}
                    handleDownloadExcel={handleDownloadExcel}
                    handleClearSelection={handleClearSelection}
                    handleJumpToDaily={handleJumpToDaily}
                    handleSaveDaily={handleSaveDaily}
                    handleSelectAll={handleSelectAll}
                    handleSelectRow={handleSelectRow}
                    handleShareStats={handleShareStats}
                    handleStatusChange={handleStatusChange}
                    handleSyncSoteria={handleSyncSoteria}
                    handleYearMonthClick={handleYearMonthClick}
                    isDownloadMenuOpen={isDownloadMenuOpen}
                    isMobile={isMobile}
                    isSoteriaLoading={isSoteriaLoading}
                    monthlyData={monthlyData}
                    searchQuery={searchQuery}
                    selectedDepartment={selectedDepartment}
                    selectedIds={selectedIds}
                    setActiveTab={setActiveTab}
                    setBulkHoverState={setBulkHoverState}
                    setCurrentDate={setCurrentDate}
                    setCurrentMonth={setCurrentMonth}
                    setCurrentYear={setCurrentYear}
                    setIsDownloadMenuOpen={setIsDownloadMenuOpen}
                    setSearchQuery={setSearchQuery}
                    setSortKey={setSortKey}
                    setSortOrder={setSortOrder}
                    setTargetGroup={setTargetGroup}
                    setViewMode={setViewMode}
                    sortKey={sortKey}
                    sortOrder={sortOrder}
                    sundaysInMonth={sundaysInMonth}
                    targetGroup={targetGroup}
                    theme={theme}
                    viewMode={viewMode}
                    yearlyData={yearlyData}
                    handleMarkDragStart={handleMarkDragStart}
                    handleSelectDragStart={handleSelectDragStart}
                    handleDragOver={handleDragOver}
                    isDragging={isDragging}
                    dragStatus={dragStatus}
                    setSelectedIds={setSelectedIds}
                />
            )}

            <AttendanceDetailModal
                onClose={() => setViewingStudent(null)}
                onShare={handleShareViewingStudent}
                targetGroup={targetGroup}
                theme={theme}
                viewingStudent={viewingStudent}
            />


        </div >
    );
}
